import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { PageLayout } from '@voilajsx/uikit/page';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@voilajsx/uikit/card';
import { Button } from '@voilajsx/uikit/button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@voilajsx/uikit/table';
import { Alert, AlertDescription } from '@voilajsx/uikit/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@voilajsx/uikit/select';
import { Badge } from '@voilajsx/uikit/badge';
import { Coins, Loader2, AlertTriangle, ArrowRight, ChevronLeft, ChevronRight, Filter, Plus } from 'lucide-react';
import { Header,  SEO } from '../../../shared/components';
import { useAuth } from '../../auth';
import { config } from '../../auth/config';
import { route } from '../../../shared/utils';

interface LedgerEntry {
  id: string;
  type: string;
  amount: number;
  description: string;
  balanceAfter: number;
  createdAt: string;
}

const TYPE_BADGE_COLORS: Record<string, string> = {
  signup_bonus: 'bg-blue-100 text-blue-800',
  purchase: 'bg-green-100 text-green-800',
  download_debit: 'bg-red-100 text-red-800',
  admin_grant: 'bg-green-100 text-green-800',
  admin_revoke: 'bg-red-100 text-red-800',
  refund: 'bg-yellow-100 text-yellow-800',
};

const TYPE_LABELS: Record<string, string> = {
  signup_bonus: 'Signup Bonus',
  purchase: 'Purchase',
  download_debit: 'Download',
  admin_grant: 'Admin Grant',
  admin_revoke: 'Admin Revoke',
  refund: 'Refund',
};

async function apiGet(endpoint: string, token: string) {
  const res = await fetch(`${config.api.baseUrl}${endpoint}`, {
    headers: {
      'Content-Type': 'application/json',
      [config.auth.headers.frontendKey]: config.auth.headers.frontendKeyValue,
      [config.auth.headers.auth]: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `HTTP ${res.status}`);
  }
  return res.json();
}

const CreditsPage: React.FC = () => {
  const { isAuthenticated, token } = useAuth();

  const [balance, setBalance] = useState<number>(0);
  const [ledger, setLedger] = useState<LedgerEntry[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  const pageSize = 20;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const fetchData = useCallback(async (currentPage: number, type?: string) => {
    if (!isAuthenticated || !token) return;
    setLoading(true);
    setError(null);

    try {
      const t = type ?? typeFilter;
      const typesParam = t && t !== 'all' ? `&types=${t}` : '';
      const [balData, ledgerData] = await Promise.all([
        apiGet('/api/credit/balance', token),
        apiGet(`/api/credit/ledger?page=${currentPage}&pageSize=${pageSize}${typesParam}`, token),
      ]);

      setBalance(balData?.data?.balance ?? 0);
      setLedger(ledgerData?.data?.entries || []);
      setTotal(ledgerData?.data?.total || 0);
    } catch (err: any) {
      setError(err?.message || 'Failed to load credit data');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, token]);

  useEffect(() => {
    fetchData(page);
  }, [page, fetchData]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (!isAuthenticated) {
    return (
      <PageLayout size="full">
        <SEO title="My Credits" description="View your credit balance and history" />
        <Header />
        <PageLayout.Content>
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Please <Link to={route('/auth/login')} className="font-medium text-primary hover:underline">sign in</Link> to view your credits.
            </AlertDescription>
          </Alert>
        </PageLayout.Content>
      </PageLayout>
    );
  }

  return (
    <PageLayout size="full">
      <SEO title="My Wallet" description="View your balance and transaction history" />
      <Header />

      <PageLayout.Content>
        <div className="space-y-6 pb-20">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">My Wallet</h1>
            <p className="text-muted-foreground">View your balance and transaction history</p>
          </div>

          {error && (
            <Alert className="bg-destructive/10 border-destructive text-destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Balance Card */}
          <Card>
            <CardContent className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6">
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
                  <Coins className="h-7 w-7 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Available Balance</p>
                  {loading ? (
                    <div className="h-9 w-24 bg-muted rounded animate-pulse" />
                  ) : (
                    <p className="text-4xl font-bold">₹{balance}</p>
                  )}
                </div>
              </div>
              <Link to={route('/credits/buy')}>
                <Button className="gap-2">
                  <Plus className="h-4 w-4" /> Top Up
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* Transaction History */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Transaction History</CardTitle>
                  <CardDescription>
                    {typeFilter !== 'all' ? 'Filtered results' : 'Your credit transactions and usage'}
                    {total > 0 && ` · ${total} entries`}
                  </CardDescription>
                </div>
                <Button
                  variant={showFilters ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="gap-2"
                >
                  <Filter className="h-4 w-4" />
                  Filter
                  {typeFilter !== 'all' && (
                    <span style={{
                      width: 18, height: 18, borderRadius: '50%', background: '#FFD93D', color: '#111',
                      fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
                    }}>!</span>
                  )}
                </Button>
              </div>

              {showFilters && (
                <div style={{
                  background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 10,
                  padding: 14, marginTop: 12, display: 'flex', gap: 10, alignItems: 'flex-end',
                }}>
                  <div style={{ flex: '1 1 180px', minWidth: 150 }}>
                    <label className="text-xs font-medium text-zinc-400 mb-1 block">Transaction Type</label>
                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                      <SelectTrigger><SelectValue placeholder="All Types" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="signup_bonus">Signup Bonus</SelectItem>
                        <SelectItem value="download_debit">Download</SelectItem>
                        <SelectItem value="admin_grant">Admin Grant</SelectItem>
                        <SelectItem value="admin_revoke">Admin Revoke</SelectItem>
                        <SelectItem value="purchase">Purchase</SelectItem>
                        <SelectItem value="refund">Refund</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    size="sm"
                    style={{ background: '#FFD93D', color: '#111' }}
                    onClick={() => { setPage(1); fetchData(1, typeFilter); }}
                  >
                    Apply
                  </Button>
                  {typeFilter !== 'all' && (
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => { setTypeFilter('all'); setPage(1); fetchData(1, 'all'); }}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              )}
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-muted-foreground">Loading transactions...</span>
                </div>
              ) : ledger.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <p className="text-muted-foreground">No transactions yet.</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto rounded-md border border-zinc-700">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-b border-zinc-700">
                          <TableHead className="border-r border-zinc-700">Date</TableHead>
                          <TableHead className="border-r border-zinc-700">Type</TableHead>
                          <TableHead className="border-r border-zinc-700 text-right">Amount</TableHead>
                          <TableHead className="border-r border-zinc-700">Description</TableHead>
                          <TableHead className="text-right">Balance After</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {ledger.map((entry) => (
                          <TableRow key={entry.id} className="border-b border-zinc-700">
                            <TableCell className="border-r border-zinc-700 whitespace-nowrap text-sm">{formatDate(entry.createdAt)}</TableCell>
                            <TableCell className="border-r border-zinc-700">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${TYPE_BADGE_COLORS[entry.type] || 'bg-gray-100 text-gray-800'}`}>
                                {TYPE_LABELS[entry.type] || entry.type}
                              </span>
                            </TableCell>
                            <TableCell className={`border-r border-zinc-700 text-right font-medium ${entry.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {entry.amount > 0 ? '+' : ''}₹{Math.abs(entry.amount)}
                            </TableCell>
                            <TableCell className="border-r border-zinc-700 text-sm text-muted-foreground">{entry.description}</TableCell>
                            <TableCell className="text-right font-medium">₹{entry.balanceAfter}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="flex items-center justify-between pt-4">
                    <p className="text-sm text-muted-foreground">Page {page} of {totalPages}</p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
                        <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                        Next <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </PageLayout.Content>

    </PageLayout>
  );
};

export default CreditsPage;
